import type { Utility } from "@cs2/contracts";
import { grenadeEffectRadiusHu } from "@cs2/physics";

/**
 * Umrechnung Hammer Units → Karteneinheiten des 0–1000-Koordinatenraums (siehe
 * Dateikopf). Einzige Quelle für Granaten-Wirkradien ist `grenadeEffectRadiusHu`
 * aus `@cs2/physics` — hier NICHT erneut hartcodieren, sonst driften Physik- und
 * Fallback-Darstellung wieder auseinander.
 */
const HU_PER_MAP_UNIT = 5;

/**
 * Gemeinsame Darstellungsregeln der Taktikkarte für beide Rendering-Stacks
 * (Konva-Editor + SVG-Quickview). Hard Rule 2: die beiden Views dürfen sich nur
 * in der Perspektive unterscheiden — Größen, Farben und Effektformen kommen
 * deshalb aus dieser Datei, nicht aus je einem Satz Literale pro Stack.
 *
 * **Alle Maße sind Karteneinheiten des 0–1000-Koordinatenraums.** Die Quickview
 * zeichnet direkt darin; der Editor rechnet mit `glyphScale()` in Pixel um.
 * Referenz ist 1920×1080: dort ist die Editor-Stage 1178 px breit
 * (1 Einheit ≈ 1,178 px) und die Quickview-Karte 1098 px × `scale(1.06)`
 * (1 Einheit ≈ 1,164 px) — nah genug, dass eine Tabelle für beide reicht.
 */
export const overlay = {
  step: {
    radius: 11,
    selectedRadius: 14,
    stroke: 2,
    selectedStroke: 2.8,
    fontSize: 11,
  },
  routeDot: { radius: 5, stroke: 1.5 },
  // Bleibt knapp unter dem eigenen Schritt: Mitspieler sind Kontext, nicht das
  // Hauptobjekt — kippt das Verhältnis, liest sich die Karte falsch herum.
  teamPlayer: { radius: 10, stroke: 1.5, fontSize: 9 },
  route: {
    teamStroke: 2.5,
    teamDash: [5, 4],
    ownStroke: 5,
    ownUnderStroke: 11,
    // Weg unter einer anderen Ebene (Underpass, Lower Tunnels)
    coveredDash: [7, 6],
    coveredOpacity: 0.55,
  },
  routeAction: { radius: 7.5, glyph: 11, stroke: 1.6 },
  utility: {
    ringRadius: 9.5,
    ringStroke: 2.5,
    glyphSize: 13,
    labelFontSize: 10,
    labelOffsetY: -27,
    throwStroke: 2.1,
    throwDash: [3.5, 5],
    handleRadius: 6,
  },
  bomb: {
    width: 29,
    height: 17,
    coreWidth: 17,
    coreHeight: 7,
    fuse: 14.5,
    tipY: 16,
    tip: 2.2,
    stroke: 1.7,
    selectedStroke: 2.5,
    cornerRadius: 1.7,
    fontSize: 9.5,
    labelOffsetY: 12,
    captionWidth: 119,
  },
  // Kartenannotation statt UI-Label: Die dichte Referenzbasis bleibt mit der
  // Marker-Schrift lesbar, ohne die Karte wie die großen Quell-Labels zu
  // dominieren.
  callout: {
    minWidth: 42,
    maxWidth: 126,
    characterWidth: 5.7,
    paddingX: 10,
    height: 19,
    fontSize: 8.7,
    letterSpacing: 0.1,
  },
  element: {
    textFontSize: 11,
    arrowStroke: 2.5,
    pointerLength: 12,
    pointerWidth: 10,
    highlightStroke: 1.3,
    highlightDash: [6, 5],
  },
  // Editor-only: Warnkreis + Hinweistext, wenn ein Wurf ohne Route/Schritt
  // platziert werden soll — die Quickview kennt diesen Zustand nicht.
  placementBlocked: {
    radius: 11,
    dash: [3.4, 3.4],
    strokeWidth: 1.7,
    labelOffsetY: 15,
    labelX: -119,
    labelWidth: 238,
    labelFontSize: 9.5,
  },
} as const;

const MIN_GLYPH_SCALE = 0.85;
const MAX_GLYPH_SCALE = 1.4;

/**
 * Einheiten → Pixel für den Editor. Die Marker folgen damit der Karte statt
 * fester Pixelgrößen. Die Untergrenze verhindert, dass Schrittzahlen auf einem
 * schmalen Fenster (Shell-`min-width` 1280 → Stage ≈ 538 px) unlesbar werden;
 * die Obergrenze, dass sie auf sehr breiten Displays die Karte zupflastern.
 */
export function glyphScale(stageWidth: number): number {
  return Math.min(
    MAX_GLYPH_SCALE,
    Math.max(MIN_GLYPH_SCALE, stageWidth / 1000),
  );
}

/**
 * Konva setzt Text an der Oberkante an, SVG an der Grundlinie. Ohne diese
 * Umrechnung säßen dieselben Beschriftungen in den beiden Stacks
 * unterschiedlich hoch.
 */
export function textBaseline(top: number, fontSize: number): number {
  return top + fontSize * 0.8;
}

export function utilityColor(type: Utility["type"]): string {
  switch (type) {
    case "C4":
      return "#ff5e59";
    case "DECOY":
      return "#73a8d7";
    case "SMOKE":
      return "#c7ccce";
    case "MOLLY":
      return "#f2aa00";
    case "FLASH":
      return "#fef3c7";
    default:
      return "#a8ee18";
  }
}

/**
 * `#rgb`/`#rrggbb` + Alpha → `rgba(...)`. Konva und SVG verstehen beide diese
 * Form. Die Kurzschreibweise muss mit: der Editor färbt die ausgewählte Utility
 * mit `#fff`, und ohne Aufklappen würde daraus ein blauer Effektkreis.
 */
export function withAlpha(color: string, alpha: number): string {
  const digits = color.slice(1);
  const value = Number.parseInt(
    digits.length === 3
      ? digits
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : digits,
    16,
  );
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export type EffectKind = "cloud" | "fire" | "burst" | "flash" | "pulse";

export interface UtilityEffect {
  /** `null` = platziertes Objekt ohne Wirkfläche (C4). */
  kind: EffectKind | null;
  radius: number;
  fillOpacity: number;
  strokeWidth: number;
  dash: number[] | null;
  /** Deckkraft von Kontur und Speichen — Standard 1, FLASH gedimmt (siehe unten). */
  opacity: number;
}

/**
 * Wirkfläche pro Granatentyp. Der Radius wird aus `grenadeEffectRadiusHu`
 * (`@cs2/physics`) abgeleitet, nicht erneut hartcodiert (siehe `HU_PER_MAP_UNIT`).
 * C4 wird gesetzt statt geworfen und bekommt deshalb weiterhin gar keine Fläche.
 */
export function utilityEffect(type: Utility["type"]): UtilityEffect {
  switch (type) {
    case "SMOKE":
      return {
        kind: "cloud",
        radius: grenadeEffectRadiusHu.SMOKE / HU_PER_MAP_UNIT,
        fillOpacity: 0.2,
        strokeWidth: 2,
        dash: null,
        opacity: 1,
      };
    case "MOLLY":
      return {
        kind: "fire",
        radius: grenadeEffectRadiusHu.MOLLY / HU_PER_MAP_UNIT,
        fillOpacity: 0.18,
        strokeWidth: 2,
        dash: [4, 3],
        opacity: 1,
      };
    case "HE":
      return {
        kind: "burst",
        radius: grenadeEffectRadiusHu.HE / HU_PER_MAP_UNIT,
        fillOpacity: 0.1,
        strokeWidth: 2,
        dash: [5, 4],
        opacity: 1,
      };
    case "FLASH":
      return {
        kind: "flash",
        radius: grenadeEffectRadiusHu.FLASH / HU_PER_MAP_UNIT,
        fillOpacity: 0,
        strokeWidth: 1.6,
        dash: [3, 5],
        // Der reale Wirkradius ist groß — ungedimmt dominiert die Kontur den
        // Bildschirm. Deutlich gedimmt, damit sie als Orientierung dient statt
        // die Karte zu überstrahlen.
        opacity: 0.35,
      };
    case "DECOY":
      return {
        kind: "pulse",
        radius: grenadeEffectRadiusHu.DECOY / HU_PER_MAP_UNIT,
        fillOpacity: 0,
        strokeWidth: 1.6,
        dash: [3, 4],
        opacity: 1,
      };
    default:
      return {
        kind: null,
        radius: 0,
        fillOpacity: 0,
        strokeWidth: 0,
        dash: null,
        opacity: 1,
      };
  }
}

export interface EffectRing {
  radius: number;
  dash: number[] | null;
  strokeWidth: number;
  opacity: number;
}

/** Zusätzliche Innenringe eines Effekts — ohne Füllung, nur Kontur. */
export function effectRings(effect: UtilityEffect): EffectRing[] {
  switch (effect.kind) {
    case "cloud":
      return [
        {
          radius: effect.radius * 0.62,
          dash: null,
          strokeWidth: 1.4,
          opacity: 0.55,
        },
      ];
    case "fire":
      return [
        {
          radius: effect.radius * 0.55,
          dash: [2.5, 3],
          strokeWidth: 1.4,
          opacity: 0.7,
        },
      ];
    case "pulse":
      return [
        {
          radius: effect.radius * 0.45,
          dash: [3, 4],
          strokeWidth: 1.4,
          opacity: 0.8,
        },
        {
          radius: effect.radius * 0.72,
          dash: [3, 4],
          strokeWidth: 1.4,
          opacity: 0.6,
        },
      ];
    default:
      return [];
  }
}

/** Speichen/Strahlen als `[x1, y1, x2, y2]` relativ zum Landepunkt. */
export function effectSpokes(
  effect: UtilityEffect,
): Array<[number, number, number, number]> {
  const layout =
    effect.kind === "burst"
      ? { count: 8, from: 0.72 }
      : effect.kind === "flash"
        ? { count: 12, from: 0.35 }
        : null;
  if (!layout) return [];
  const spokes: Array<[number, number, number, number]> = [];
  for (let index = 0; index < layout.count; index += 1) {
    const angle = (index * 2 * Math.PI) / layout.count;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    spokes.push([
      cos * effect.radius * layout.from,
      sin * effect.radius * layout.from,
      cos * effect.radius,
      sin * effect.radius,
    ]);
  }
  return spokes;
}

/** Canvas needs resolved CSS values; the production tokens own its theme. */
export function mapThemeColor(token: "--route" | "--accent" | "--bg"): string {
  const fallback = token === "--bg" ? "#101719" : utilityColor("HE");
  return typeof document === "undefined"
    ? fallback
    : getComputedStyle(document.documentElement)
        .getPropertyValue(token)
        .trim() || fallback;
}
